import { Form, Input, InputNumber, Select } from "antd";
import { CustomFieldProps, RecordModel } from "../core";
import { IDesignLevel, LevelLayout } from "./floorPlans";

export const LAYOUT_OPTIONS: { value: LevelLayout, label: string }[] = [
  { value: '', label: 'Let the drawing decide' },
  { value: 'rows', label: 'Rows (plan above, copy below)' },
  { value: 'columns', label: 'Columns (slim plan left, copy right)' },
];

const layoutLabel = (value: LevelLayout) =>
  value ? LAYOUT_OPTIONS.find(o => o.value === value)?.label.split(' (')[0] : 'From the drawing';

/*
 * Every field is custom, so the form keeps the serializer's order (see
 * PaymentPlanFields for why a mix of custom and generated fields does not).
 */
const hidden = (props: CustomFieldProps<IDesignLevel>) => (
  <Form.Item name={props.formName} hidden><InputNumber /></Form.Item>
);

const nothing = () => null;

const name = (label: string, placeholder: string) => (props: CustomFieldProps<IDesignLevel>) => (
  <Form.Item name={props.formName} label={label}>
    <Input maxLength={128} placeholder={placeholder} />
  </Form.Item>
);

/**
 * The storeys of a design, edited from the design's drawer.
 *
 * A level is created and named here; its floor plan is made in the floor-plan
 * wizard, which asks for the level on a design with more than one. The server
 * refuses to delete the last level, or one that still has published plans or
 * unit overrides, and says so.
 */
export const DesignLevelModel: RecordModel<IDesignLevel> = {
  name: "Level",
  namePlural: "Levels",
  path: "design-levels",
  selectQuery: "",
  tableQuery: "",
  toString: (r: IDesignLevel) => r.name || `Level ${r.index + 1}`,
  defaultValues: { layout_override: '' },
  customTableMappings: {
    index: {
      title: 'Order', sort_index: 0, width: 70,
      render: r => r.index,
    },
    name: {
      title: 'Name', sort_index: 1,
      render: r => r.name || <span style={{ color: 'var(--ancana-gray)' }}>{`Level ${r.index + 1} (unnamed)`}</span>,
    },
    layout_override: {
      title: 'Brochure layout', sort_index: 2,
      render: r => layoutLabel(r.layout_override),
    },
    current_floor_plan: {
      title: 'Floor plan', sort_index: 3,
      render: r => (r.current_floor_plan ? 'Published' : 'None yet'),
    },
    name__en: { omit: true },
    name__es: { omit: true },
  },
  customCreateFields: [
    { name: 'design', component: hidden },
    {
      name: 'index',
      component: (props: CustomFieldProps<IDesignLevel>) => (
        <Form.Item name={props.formName} label="Order"
                   rules={[{ required: true, message: 'Give the level its place, 0 for the lowest.' }]}
                   extra="Bottom to top: 0 is the lowest level anybody sells, not a floor number.">
          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
        </Form.Item>
      ),
    },
    { name: 'current_floor_plan', component: nothing },
    {
      name: 'layout_override',
      component: (props: CustomFieldProps<IDesignLevel>) => (
        <Form.Item name={props.formName} label="Brochure layout"
                   extra="How this level's floor-plan slide is laid out. Left alone, the shape of the plan decides.">
          <Select options={LAYOUT_OPTIONS} />
        </Form.Item>
      ),
    },
    { name: 'name', component: nothing },
    { name: 'name__en', component: name('Name (English)', 'Ground floor') },
    { name: 'name__es', component: name('Name (Spanish)', 'Planta baja') },
  ],
};
